import { Children, Fragment, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { ShareArticle } from "@/components/ShareArticle";
import { NetworkNextStep, type NextStepKey, type NextStepDeepLink } from "@/components/NetworkNextStep";
import { AlsoOnShrinkopedia } from "@/components/AlsoOnShrinkopedia";
import { AlsoOnShrinktionary } from "@/components/AlsoOnShrinktionary";
import { Link } from "wouter";
import { citationsToSchema, EDITORIAL_PROCESS_URL, personSchema, type CitationInput } from "@/lib/seo";
import { CRISIS_LIFELINE } from "@/lib/links";
import { withNetworkUtm } from "@/lib/network";
import { ReviewerPanel, CiteThisPage, EvidenceLabel } from "@/components/ReviewerPanel";

export interface PerspectiveFaq {
  q: string;
  a: string;
}

export interface PerspectiveRelated {
  title: string;
  href: string;
}

export interface PerspectiveArticleProps {
  slug: string;
  category: string;
  title: string;
  seoTitle: string;
  metaDescription: string;
  quickAnswer: string;
  publishDate: string;
  publishDateLabel: string;
  readTime: string;
  keywords: string[];
  about: string[];
  faqs: PerspectiveFaq[];
  related: PerspectiveRelated[];
  /** Optional per-article social image. Defaults to /og-perspectives.jpg. */
  ogImage?: string;
  /** Optional in-article editorial image rendered before the FAQ section. */
  inlineImage?: string;
  /** Alt text for the inline editorial image. Recommended when inlineImage is set. */
  inlineImageAlt?: string;
  /** Optional approximate word count for richer Article schema signal. */
  wordCount?: number;
  /** Optional date the article was medically reviewed. Defaults to publishDate. */
  lastReviewed?: string;
  /** Optional human-readable label for the medically reviewed date. */
  lastReviewedLabel?: string;
  /** When true, renders the composite-patient narrative disclosure before the FAQ section. */
  compositeNarrative?: boolean;
  /** Optional scholarly citations emitted into the BlogPosting JSON-LD `citation` array. */
  citations?: CitationInput[];
  /** Optional article tags (used for og article:tag meta entries). */
  articleTags?: string[];
  /**
   * Optional care-routing block. Set this only on essays that
   * meaningfully concern symptoms or clinical patterns. Renders a short
   * 'If this pattern is affecting you' section pointing at the
   * /work-with-dr-refai bridge page. Do NOT set on meta essays
   * (e.g. 'what modern psychiatry gets right'), per the site's
   * editorial directive: link to clinical care only when the essay
   * meaningfully concerns clinical care or psychiatric access.
   */
  careConsideration?: boolean;
  /**
   * Reciprocal "Related reading on shrinkMD" links (shrinkMD blog posts that
   * link back to this essay). Rendered as one plain sentence each near the
   * end of the main content. Hrefs are used exactly as given, UTMs included.
   */
  shrinkmdReading?: { before: string; anchor: string; after: string; href: string }[];
  /** Evidence label shown under the reviewer panel. Defaults to "educational". */
  evidenceLevel?: "strong" | "emerging" | "educational";
  /** Optional "Further Reading" block rendered after Related Perspectives and before the Author Box. */
  furtherReading?: ReactNode;
  children: ReactNode;
}

const SITE_ORIGIN = "https://shariqrefai.com";
const PERSON_ID = `${SITE_ORIGIN}/#person`;
const PUBLISHER_ID = `${SITE_ORIGIN}/#publisher`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const LOGO_ID = `${SITE_ORIGIN}/#logo`;

// Use the canonical Person schema from src/lib/seo.ts so the article graph
// stays in sync with global person data (medicalSpecialty, affiliation,
// award, identifier, publishingPrinciples, expanded sameAs).
const { "@context": _personContext, ...PERSON_NODE_BASE } = personSchema as Record<string, any>;
const PERSON_NODE = PERSON_NODE_BASE;

const LOGO_NODE = {
  "@type": "ImageObject",
  "@id": LOGO_ID,
  url: `${SITE_ORIGIN}/favicon-512.png`,
  contentUrl: `${SITE_ORIGIN}/favicon-512.png`,
  width: 512,
  height: 512,
  caption: "Shariq Refai, MD, MBA",
};

const PUBLISHER_NODE = {
  "@type": "Organization",
  "@id": PUBLISHER_ID,
  name: "Shariq Refai, MD, MBA",
  url: SITE_ORIGIN,
  logo: { "@id": LOGO_ID },
  founder: { "@id": PERSON_ID },
  memberOf: { "@id": "https://shrinknetwork.com/#organization" },
  sameAs: [
    "https://www.linkedin.com/in/shariqrefai",
    "https://shrinkmd.com",
  ],
};

const WEBSITE_NODE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_ORIGIN,
  name: "Shariq Refai, MD, MBA",
  publisher: { "@id": PUBLISHER_ID },
  inLanguage: "en-US",
};

const BLOG_ID = `${SITE_ORIGIN}/perspectives#blog`;

// Context-aware next-step destinations per essay, capped at three. Per the
// network spec, every set ALWAYS includes Shrinkopedia (concept-layer
// catch-all) and shrinkMD ("Get clinical care"); the first slot is the
// topic-matched destination for that essay.
const NEXT_STEP_DEFAULT: NextStepKey[] = ["anxietyresource", "shrinkopedia", "shrinkmd"];

const NEXT_STEP_BY_SLUG: Record<string, NextStepKey[]> = {
  "digital-mental-health-needs-more-than-convenience": ["shrinkiatry", "shrinkopedia", "shrinkmd"],
  "what-modern-psychiatry-gets-right": ["shrinkiatry", "shrinkopedia", "shrinkmd"],
  "why-popular-mental-health-commentary-falls-short-in-the-moment": ["shrinq", "shrinkopedia", "shrinkmd"],
  "why-high-performers-can-still-feel-anxious": ["anxietyresource", "shrinkopedia", "shrinkmd"],
  "difference-between-a-thought-and-thinking": ["shrinktionary", "shrinkopedia", "shrinkmd"],
  "why-your-mind-feels-loudest-when-trying-to-rest": ["anxietyresource", "shrinkopedia", "shrinkmd"],
  "why-thinking-harder-usually-makes-anxiety-worse": ["shrinq", "shrinkopedia", "shrinkmd"],
  "why-your-brain-replays-conversations-long-after-they-end": ["shrinktionary", "shrinkopedia", "shrinkmd"],
  "why-your-nervous-system-thinks-everything-is-urgent": ["anxietyresource", "shrinkopedia", "shrinkmd"],
};

// Topic deep links for each essay's next-step cards. Every URL is listed in
// its site's sitemap (checked 2026-09-24). The card for a destination not
// listed here falls back to that site's "start here" hub.
const NEXT_STEP_DEEP_LINKS: Record<string, Partial<Record<NextStepKey, NextStepDeepLink>>> = {
  "digital-mental-health-needs-more-than-convenience": {
    shrinkiatry: { href: "https://shrinkiatry.com/technology/what-telepsychiatry-changes/", label: "What telepsychiatry changes, at shrinkiatry" },
    shrinkmd: { href: "https://shrinkmd.com/telepsychiatry", label: "How telepsychiatry works at shrinkMD" },
  },
  "what-modern-psychiatry-gets-right": {
    shrinkiatry: { href: "https://shrinkiatry.com/decoder/why-a-diagnosis-can-change/", label: "Why a diagnosis can change, at shrinkiatry" },
    shrinkopedia: { href: "https://shrinkopedia.com/terms/differential-diagnosis/", label: "Differential diagnosis at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/psychiatric-evaluation", label: "What a psychiatric evaluation involves at shrinkMD" },
  },
  "why-popular-mental-health-commentary-falls-short-in-the-moment": {
    shrinq: { href: "https://shrinq.com/free-tools/panic-reset-guide/", label: "The panic reset guide at shrinQ" },
    shrinkopedia: { href: "https://shrinkopedia.com/library/window-of-tolerance/", label: "The window of tolerance at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/panic-disorder", label: "Care for panic disorder at shrinkMD" },
  },
  "why-high-performers-can-still-feel-anxious": {
    anxietyresource: { href: "https://anxietyresource.org/work-performance/high-functioning-anxiety/", label: "High-functioning anxiety at AnxietyResource" },
    shrinkopedia: { href: "https://shrinkopedia.com/library/yerkes-dodson-curve/", label: "The Yerkes-Dodson curve at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/anxiety-disorders", label: "Care for anxiety at shrinkMD" },
  },
  "difference-between-a-thought-and-thinking": {
    shrinktionary: { href: "https://shrinktionary.com/terms/intrusive-thought/", label: "Intrusive thought, defined at Shrinktionary" },
    shrinkopedia: { href: "https://shrinkopedia.com/treatments/acceptance-and-commitment-therapy/", label: "Acceptance and commitment therapy at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/anxiety-disorders", label: "Care for anxiety at shrinkMD" },
  },
  "why-your-mind-feels-loudest-when-trying-to-rest": {
    anxietyresource: { href: "https://anxietyresource.org/resources/anxiety-keeping-you-up-at-night/", label: "When anxiety keeps you up at night, at AnxietyResource" },
    shrinkopedia: { href: "https://shrinkopedia.com/symptoms/racing-thoughts/", label: "Racing thoughts at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/insomnia", label: "Care for insomnia at shrinkMD" },
  },
  "why-thinking-harder-usually-makes-anxiety-worse": {
    shrinq: { href: "https://shrinq.com/patterns/rumination-loop/", label: "The rumination loop at shrinQ" },
    shrinkopedia: { href: "https://shrinkopedia.com/library/rumination/", label: "Rumination at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/generalized-anxiety-disorder", label: "Care for generalized anxiety at shrinkMD" },
  },
  "why-your-brain-replays-conversations-long-after-they-end": {
    shrinktionary: { href: "https://shrinktionary.com/terms/rumination/", label: "Rumination, defined at Shrinktionary" },
    shrinkopedia: { href: "https://shrinkopedia.com/conditions/social-anxiety-disorder/", label: "Social anxiety disorder at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/social-anxiety-disorder", label: "Care for social anxiety at shrinkMD" },
  },
  "why-your-nervous-system-thinks-everything-is-urgent": {
    anxietyresource: { href: "https://anxietyresource.org/panic-nervous-system/hypervigilance/", label: "Hypervigilance at AnxietyResource" },
    shrinkopedia: { href: "https://shrinkopedia.com/library/stress-response/", label: "The stress response at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/anxiety-disorders", label: "Care for anxiety at shrinkMD" },
  },
  "why-we-present-care-as-five-options-not-one": {
    anxietyresource: { href: "https://anxietyresource.org/start-here/", label: "Start here at AnxietyResource" },
    shrinkopedia: { href: "https://shrinkopedia.com/where-to-start/", label: "Where to start at Shrinkopedia" },
    shrinkmd: { href: "https://shrinkmd.com/how-it-works", label: "How care works at shrinkMD" },
  },
};

/** Plain text of a React node, for heading anchors and the contents list. */
function nodeText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return "";
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u2019']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

interface SectionAnchor {
  id: string;
  text: string;
}

/**
 * Give every essay <h2> a stable id (keeping any id the essay already set)
 * and collect them for the "On this page" list. Runs during render, so the
 * ids are identical in the prerendered HTML and the first client render.
 * These ids are the deep links AI answers and search results can point at.
 */
function anchorSections(children: ReactNode): { content: ReactNode; sections: SectionAnchor[] } {
  const sections: SectionAnchor[] = [];
  const used = new Set<string>();
  const walk = (node: ReactNode): ReactNode =>
    Children.map(node, (child) => {
      if (!isValidElement(child)) return child;
      const el = child as ReactElement<{ id?: string; children?: ReactNode }>;
      if (el.type === "h2") {
        const text = nodeText(el.props.children).trim();
        let id = el.props.id || slugifyHeading(text) || `section-${sections.length + 1}`;
        while (used.has(id)) id = `${id}-${sections.length + 1}`;
        used.add(id);
        sections.push({ id, text });
        return el.props.id === id ? el : cloneElement(el, { id });
      }
      if (el.type === Fragment) {
        return cloneElement(el, {}, walk(el.props.children));
      }
      return el;
    });
  const content = walk(children);
  return { content, sections };
}

function readTimeToIso(readTime: string): string | undefined {
  const m = readTime.match(/(\d+)\s*min/i);
  return m ? `PT${m[1]}M` : undefined;
}

const MENTIONS_NODES = [
  {
    "@type": "MedicalOrganization",
    name: "shrinkMD",
    url: "https://shrinkmd.com",
    sameAs: ["https://shrinkmd.com/shariq-refai"],
  },
  {
    "@type": "MobileApplication",
    name: "Unstuck",
    operatingSystem: "iOS",
    applicationCategory: "HealthApplication",
    url: "https://beunstuck.app",
    downloadUrl: "https://apps.apple.com/us/app/unstuck-app/id6766962969",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
];

export function PerspectiveArticle({
  slug,
  category,
  title,
  seoTitle,
  metaDescription,
  quickAnswer,
  publishDate,
  publishDateLabel,
  readTime,
  keywords,
  about,
  faqs,
  related,
  ogImage,
  inlineImage,
  inlineImageAlt,
  wordCount,
  lastReviewed,
  compositeNarrative,
  lastReviewedLabel,
  citations,
  articleTags,
  careConsideration = false,
  evidenceLevel = "educational",
  shrinkmdReading,
  furtherReading,
  children,
}: PerspectiveArticleProps) {
  const { content: anchoredChildren, sections } = anchorSections(children);
  const REVIEWED_DATE = lastReviewed ?? publishDate;
  const REVIEWED_LABEL = lastReviewedLabel ?? publishDateLabel;
  const nextStepDestinations = NEXT_STEP_BY_SLUG[slug] ?? NEXT_STEP_DEFAULT;
  const ARTICLE_URL = `${SITE_ORIGIN}/perspectives/${slug}`;
  const HERO_IMAGE_SRC = ogImage ?? "/og-perspectives.jpg";
  const SOCIAL_IMAGE = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${SITE_ORIGIN}${ogImage}`)
    : `${SITE_ORIGIN}/og-perspectives.jpg`;
  const INLINE_IMAGE_ABS = inlineImage
    ? (inlineImage.startsWith("http") ? inlineImage : `${SITE_ORIGIN}${inlineImage}`)
    : null;
  // Read time comes from the word count when one is supplied (about 230
  // words a minute), so the visible label and timeRequired always agree.
  const effectiveReadTime =
    wordCount && wordCount > 0 ? `${Math.max(1, Math.round(wordCount / 230))} min read` : readTime;
  const timeRequired = readTimeToIso(effectiveReadTime);
  const publishYear = new Date(publishDate).getUTCFullYear();

  const imageNode = {
    "@type": "ImageObject",
    "@id": `${ARTICLE_URL}#primaryImage`,
    url: SOCIAL_IMAGE,
    contentUrl: SOCIAL_IMAGE,
    width: 1200,
    height: 630,
    caption: title,
  };

  const inlineImageNode = INLINE_IMAGE_ABS
    ? {
        "@type": "ImageObject",
        "@id": `${ARTICLE_URL}#inlineImage`,
        url: INLINE_IMAGE_ABS,
        contentUrl: INLINE_IMAGE_ABS,
        width: 1600,
        height: 900,
        caption: inlineImageAlt ?? title,
      }
    : null;

  const webPageNode = {
    "@type": "WebPage",
    "@id": `${ARTICLE_URL}#webpage`,
    url: ARTICLE_URL,
    name: seoTitle,
    description: metaDescription,
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    primaryImageOfPage: { "@id": `${ARTICLE_URL}#primaryImage` },
    breadcrumb: { "@id": `${ARTICLE_URL}#breadcrumb` },
    about: { "@id": PERSON_ID },
    datePublished: publishDate,
    dateModified: publishDate,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".quick-answer", ".faq"],
    },
    lastReviewed: REVIEWED_DATE,
    reviewedBy: { "@id": PERSON_ID },
    publisher: { "@id": PUBLISHER_ID },
    publishingPrinciples: EDITORIAL_PROCESS_URL,
  };

  const blogNode: Record<string, any> = {
    "@type": ["BlogPosting", "Article"],
    "@id": ARTICLE_URL,
    url: ARTICLE_URL,
    headline: title,
    name: title,
    description: metaDescription,
    abstract: quickAnswer,
    articleSection: category,
    keywords,
    inLanguage: "en-US",
    isAccessibleForFree: true,
    datePublished: publishDate,
    dateModified: publishDate,
    copyrightYear: publishYear,
    copyrightHolder: { "@id": PERSON_ID },
    author: { "@id": PERSON_ID },
    creator: { "@id": PERSON_ID },
    publisher: { "@id": PUBLISHER_ID },
    image: { "@id": `${ARTICLE_URL}#primaryImage` },
    mainEntityOfPage: { "@id": `${ARTICLE_URL}#webpage` },
    isPartOf: { "@id": BLOG_ID },
    about: about.map((name) => ({ "@type": "Thing", name })),
    mentions: MENTIONS_NODES,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".quick-answer", ".faq"],
    },
    potentialAction: {
      "@type": "ReadAction",
      target: [ARTICLE_URL],
    },
    lastReviewed: REVIEWED_DATE,
    reviewedBy: { "@id": PERSON_ID },
    editor: { "@id": PERSON_ID },
    publishingPrinciples: EDITORIAL_PROCESS_URL,
  };
  if (sections.length > 0) {
    blogNode.hasPart = sections.map((sec) => ({
      "@type": "WebPageElement",
      name: sec.text,
      url: `${ARTICLE_URL}/#${sec.id}`,
    }));
  }
  if (timeRequired) blogNode.timeRequired = timeRequired;
  if (wordCount && wordCount > 0) blogNode.wordCount = wordCount;
  if (citations && citations.length > 0) {
    blogNode.citation = citationsToSchema(citations);
  }

  const faqNode = {
    "@type": "FAQPage",
    "@id": `${ARTICLE_URL}#faq`,
    inLanguage: "en-US",
    isPartOf: { "@id": ARTICLE_URL },
    about: { "@id": PERSON_ID },
    mainEntity: faqs.map((f, i) => ({
      "@type": "Question",
      "@id": `${ARTICLE_URL}#faq-${i + 1}`,
      name: f.q,
      inLanguage: "en-US",
      answerCount: 1,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
        inLanguage: "en-US",
        author: { "@id": PERSON_ID },
      },
    })),
  };

  const breadcrumbNode = {
    "@type": "BreadcrumbList",
    "@id": `${ARTICLE_URL}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Perspectives", item: `${SITE_ORIGIN}/perspectives` },
      { "@type": "ListItem", position: 3, name: title, item: ARTICLE_URL },
    ],
  };

  const graphSchema = {
    "@context": "https://schema.org",
    "@graph": [
      PERSON_NODE,
      PUBLISHER_NODE,
      LOGO_NODE,
      WEBSITE_NODE,
      imageNode,
      ...(inlineImageNode ? [inlineImageNode] : []),
      webPageNode,
      blogNode,
      faqNode,
      breadcrumbNode,
    ],
  };

  return (
    <SiteLayout>
      <SEO
        title={seoTitle}
        description={metaDescription}
        url={ARTICLE_URL}
        image={SOCIAL_IMAGE}
        type="article"
        schema={graphSchema}
        articlePublishedTime={`${publishDate}T00:00:00-04:00`}
        articleModifiedTime={`${REVIEWED_DATE}T00:00:00-04:00`}
        articleSection={category}
        articleTags={articleTags ?? keywords.slice(0, 5)}
      />

      <article className="bg-background">
        <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
          <div className="container mx-auto max-w-3xl">
            <nav
              aria-label="Breadcrumb"
              className="mb-8 text-xs tracking-widest uppercase text-muted-foreground"
            >
              <Link href="/perspectives" className="hover:text-accent transition-colors">
                Perspectives
              </Link>
              <span className="mx-2">/</span>
              <span>{category}</span>
            </nav>
            <span className="text-accent text-sm font-bold tracking-widest uppercase">
              {category}
            </span>
            <h1 className="mt-4 text-3xl md:text-5xl font-serif text-primary leading-tight">
              {title}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground font-light">
              <span>
                By{" "}
                <Link href="/about" className="hover:text-accent transition-colors">
                  Shariq Refai, MD, MBA
                </Link>
              </span>
              <span aria-hidden="true">·</span>
              <time dateTime={publishDate}>{publishDateLabel}</time>
              <span aria-hidden="true">·</span>
              <span>{effectiveReadTime}</span>
            </div>
            <ReviewerPanel
              reviewedDate={REVIEWED_DATE}
              reviewedLabel={REVIEWED_LABEL}
              className="mt-6"
            />
            <EvidenceLabel level={evidenceLevel} className="mt-3" />
          </div>
        </header>

        <figure className="bg-muted/20 border-b border-border/50">
          <div className="container mx-auto max-w-4xl px-4 md:px-8 py-8 md:py-10">
            <img
              src={HERO_IMAGE_SRC}
              alt={title}
              width={1200}
              height={630}
              loading="eager"
              decoding="async"
              className="w-full h-auto rounded-sm shadow-sm"
            />
          </div>
        </figure>

        <div className="py-16 md:py-20 px-4 md:px-8">
          <div className="container mx-auto max-w-3xl">
            <p className="text-base text-secondary font-light leading-relaxed mb-8">
              <strong className="text-primary font-medium">By Shariq Refai, MD, MBA.</strong>{" "}
              board-certified psychiatrist, founder of{" "}
              <a
                href={withNetworkUtm("https://shrinkmd.com/", "PerspectiveArticle_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-primary transition-colors"
              >
                shrinkMD
              </a>
              , and author. This essay is general educational and editorial content. It is
              not medical advice or psychiatric treatment.
            </p>

            <ShareArticle
              url={ARTICLE_URL}
              title={title}
              description={metaDescription}
            />

            <aside
              aria-labelledby="quick-answer-heading"
              className="quick-answer mt-12 mb-16 border-l-4 border-accent bg-[#EFECE6] p-6 md:p-8"
            >
              <h2
                id="quick-answer-heading"
                className="text-xs font-bold tracking-widest uppercase text-accent mb-3"
              >
                Quick Answer
              </h2>
              <p className="text-base md:text-lg font-light text-primary leading-relaxed">
                {quickAnswer}
              </p>
            </aside>

            {sections.length >= 3 ? (
              <nav
                aria-labelledby="on-this-page-heading"
                className="on-this-page mb-12 border-l-2 border-border pl-6"
              >
                <h2
                  id="on-this-page-heading"
                  className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3"
                >
                  On this page
                </h2>
                <ol className="space-y-2 list-decimal pl-5 text-sm text-secondary font-light">
                  {sections.map((sec) => (
                    <li key={sec.id}>
                      <a href={`#${sec.id}`} className="hover:text-accent transition-colors">
                        {sec.text}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            ) : null}

            <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-6 prose-h3:text-xl prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary prose-strong:font-medium">
              {anchoredChildren}
            </div>

            <AlsoOnShrinktionary slug={slug} className="mt-12" />

            <AlsoOnShrinkopedia slug={slug} variant="essay" className="mt-6" />

            {inlineImage ? (
              <figure className="mt-16 -mx-4 md:mx-0">
                <div className="bg-[#EFECE6] p-2 md:p-3">
                  <img
                    src={inlineImage}
                    alt={inlineImageAlt ?? title}
                    loading="lazy"
                    decoding="async"
                    width={1600}
                    height={900}
                    className="w-full h-auto aspect-[16/9] object-cover"
                  />
                </div>
              </figure>
            ) : null}

            {compositeNarrative ? (
              <aside
                role="note"
                aria-label="Composite patient narrative disclosure"
                className="composite-disclaimer mt-16 border-l-2 border-accent pl-6 py-4 bg-muted/30"
              >
                <p className="text-sm text-secondary font-light leading-relaxed">
                  <strong className="text-primary font-medium">A note on patient examples.</strong>{" "}
                  Any patient examples, "I had a patient" framings, or
                  clinical-style anecdotes in this article are educational
                  composites. They blend and alter details from multiple
                  situations and do not describe any identifiable individual or
                  any actual patient of Dr. Refai or shrinkMD. They are
                  presented for educational illustration only.
                </p>
              </aside>
            ) : null}

            {citations && citations.length > 0 ? (
              <section
                aria-labelledby="references-heading"
                className="references mt-20 border-t border-border pt-10"
              >
                <h2
                  id="references-heading"
                  className="text-3xl font-serif text-primary mb-6"
                >
                  References
                </h2>
                <ol className="space-y-4 list-decimal pl-6 text-base text-secondary font-light leading-relaxed">
                  {citations.map((c, i) => (
                    <li key={i} id={`ref-${i + 1}`}>
                      {c.author ? <>{c.author}. </> : null}
                      {c.url ? (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:text-primary transition-colors"
                        >
                          {c.name}
                        </a>
                      ) : (
                        <span>{c.name}</span>
                      )}
                      {c.journal ? (
                        <>
                          . <em>{c.journal}</em>
                        </>
                      ) : null}
                      {c.datePublished ? <>. {c.datePublished}</> : null}
                      .
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            <section
              aria-labelledby="faq-heading"
              className="faq mt-20"
            >
              <h2
                id="faq-heading"
                className="text-3xl font-serif text-primary border-b border-border pb-4 mb-8"
              >
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((f, i) => (
                  <details
                    key={i}
                    id={`faq-${i + 1}`}
                    open
                    className="group border-l-2 border-accent pl-6 py-2"
                  >
                    <summary className="font-serif text-xl text-primary cursor-pointer list-none marker:hidden">
                      <h3 className="inline font-normal">{f.q}</h3>
                    </summary>
                    <p className="mt-3 text-base text-secondary font-light leading-relaxed">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            <section
              aria-labelledby="related-heading"
              className="mt-20 bg-[#EFECE6] p-8 md:p-10 border-l-4 border-accent"
            >
              <h2
                id="related-heading"
                className="font-serif text-2xl text-primary mb-4"
              >
                Related Perspectives
              </h2>
              <ul className="space-y-2 list-none p-0">
                {related.map((r, i) => (
                  <li key={i}>
                    <Link
                      href={r.href}
                      className="text-accent hover:text-primary transition-colors"
                    >
                      {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            {shrinkmdReading && shrinkmdReading.length > 0 ? (
              <section
                aria-labelledby="shrinkmd-reading-heading"
                className="shrinkmd-reading mt-12 border-l-2 border-accent/40 pl-6"
              >
                <h2
                  id="shrinkmd-reading-heading"
                  className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3"
                >
                  Related reading on shrinkMD
                </h2>
                {shrinkmdReading.map((r) => (
                  <p key={r.href} className="text-base text-secondary font-light leading-relaxed mt-2">
                    {r.before}
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                    >
                      {r.anchor}
                    </a>
                    {r.after}
                  </p>
                ))}
                <p className="mt-3 text-xs text-muted-foreground font-light">
                  shrinkMD is part of The Shrink Network, founded by Shariq Refai, MD.
                </p>
              </section>
            ) : null}

            {careConsideration ? (
              <aside
                aria-labelledby="care-consideration-heading"
                className="care-consideration mt-16 border-l-4 border-accent bg-muted/40 p-6 md:p-8"
              >
                <h2
                  id="care-consideration-heading"
                  className="font-serif text-xl md:text-2xl text-primary mb-3 leading-snug"
                >
                  If this pattern is affecting you
                </h2>
                <p className="text-secondary font-light leading-relaxed">
                  Reading a psychiatrist's essay is education. It's not
                  evaluation, and it isn't a substitute for individualized
                  care. If a pattern like this one is showing up in your
                  daily life, evaluation by a psychiatrist is a reasonable
                  next step.{" "}
                  <a
                    href="/work-with-dr-refai"
                    className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                  >
                    How to work with Dr. Refai at shrinkMD
                  </a>{" "}
                  explains how his multistate telepsychiatry practice works,
                  which states it serves, who's a good fit, and how to start.
                  It also links to alternatives if shrinkMD isn't the right
                  match. In an emergency, call or text 988 in the U.S. or call
                  911.
                </p>
                <p className="mt-3 text-xs text-muted-foreground font-light leading-relaxed">
                  Financial disclosure: shrinkMD is a clinical practice
                  founded by Dr. Refai, who has a material ownership
                  interest in the entity that operates it.
                </p>
              </aside>
            ) : null}

            {furtherReading ? (
              <section
                aria-labelledby="further-reading-heading"
                className="further-reading mt-16 border-t border-border pt-10 prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-0 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary"
              >
                <h2 id="further-reading-heading">Further Reading</h2>
                {furtherReading}
              </section>
            ) : null}

            <div className="mt-16 border-t border-border pt-10">
              <NetworkNextStep
                destinations={nextStepDestinations}
                deepLinks={NEXT_STEP_DEEP_LINKS[slug]}
                campaign={`essay_${slug.replace(/-/g, "_")}`}
              />
            </div>

            <CiteThisPage
              title={title}
              url={`${ARTICLE_URL}/`}
              year={publishYear}
              className="mt-12 border-t border-border pt-10"
            />

            <section
              aria-label="About the author"
              className="mt-12 border-t border-border pt-10"
            >
              <h2 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">
                About the Author
              </h2>
              <p className="text-base text-secondary font-light leading-relaxed">
                <Link
                  href="/about"
                  className="text-primary font-medium hover:text-accent transition-colors"
                >
                  Shariq Refai, MD, MBA
                </Link>
                , is a board-certified psychiatrist, founder of{" "}
                <a
                  href={withNetworkUtm("https://shrinkmd.com/", "PerspectiveArticle_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-primary transition-colors"
                >
                  shrinkMD
                </a>
                , founder of shrinQ, creator of the Unstuck app, author, and mental health educator based in
                Jacksonville, Florida. shariqrefai.com is an educational and editorial
                platform featuring books, essays, commentary, and media perspectives. For
                clinical care inquiries, please visit{" "}
                <a
                  href={withNetworkUtm("https://shrinkmd.com/", "PerspectiveArticle_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-primary transition-colors"
                >
                  shrinkmd.com
                </a>
                .
              </p>
            </section>

            <aside
              role="note"
              aria-label="Educational disclaimer"
              className="global-disclaimer mt-12 border border-border bg-muted/40 p-6 text-sm text-secondary font-light leading-relaxed"
            >
              <strong className="font-medium text-primary block mb-2">
                Educational Disclaimer
              </strong>
              <p>
                This article is for general educational and informational purposes only and
                does not provide medical advice, diagnosis, treatment, therapy, or a
                physician-patient relationship.
              </p>
              <p className="mt-2">
                Viewing this website, reading its content, or submitting information through
                the website does not establish a physician-patient relationship.
              </p>
              <p className="mt-2">
                If you are in crisis or feel unsafe, call or text{" "}
                <a
                  href={CRISIS_LIFELINE.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-primary transition-colors"
                >
                  988
                </a>{" "}
                in the United States, call 911, or go to the nearest emergency room.
              </p>
            </aside>

            <div className="mt-12">
              <Link
                href="/perspectives"
                className="inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
              >
                ← Back to Perspectives
              </Link>
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}
